<script lang="ts">
  import type { RoomState } from '../room/state.svelte';
  import { identitySuffix } from '../session/username';
  import { presenceLabel } from './format';

  const { room }: { room: RoomState } = $props();

  const participants = $derived(room.participants);
  const you = $derived(room.you);
  const canAdminister = $derived(room.permissions?.canAdminister === true && room.live);
  /** Duplicate display names are allowed; show a short identity to tell them apart. */
  const duplicated = $derived(
    new Set(
      participants
        .filter(
          (participant, index) =>
            participants.findIndex((other) => other.username === participant.username) !== index,
        )
        .map((participant) => participant.username),
    ),
  );
</script>

<section aria-labelledby="participants-heading">
  <h2 id="participants-heading">Watching ({participants.length})</h2>
  <ul>
    {#each participants as participant (participant.identity)}
      <li>
        <div>
          <p class="name">
            {participant.username}
            {#if duplicated.has(participant.username)}
              <span class="suffix">#{identitySuffix(participant.identity)}</span>
            {/if}
            {#if participant.identity === you?.identity}<span class="badge">you</span>{/if}
            {#if participant.is_host}<span class="badge host">host</span>{/if}
            {#if participant.is_controller}<span class="badge">controller</span>{/if}
          </p>
          <p class="presence" data-presence={participant.presence}>
            {presenceLabel(participant.presence)}
          </p>
        </div>
        {#if canAdminister && !participant.is_host}
          <button
            type="button"
            onclick={() =>
              void room.run({
                type: participant.is_controller ? 'revoke-controller' : 'grant-controller',
                identity: participant.identity,
              })}
          >
            {participant.is_controller ? 'Revoke control' : 'Make controller'}
          </button>
        {/if}
      </li>
    {/each}
  </ul>
  {#if participants.length === 0}
    <p class="empty">Nobody else is here yet.</p>
  {/if}
</section>

<style>
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  li {
    display: flex;
    gap: 0.6rem;
    align-items: center;
    justify-content: space-between;
  }

  .name {
    margin: 0;
    overflow-wrap: anywhere;
  }

  .presence {
    margin: 0;
    font-size: 0.82rem;
    color: var(--wa-text-dim);
  }

  .presence[data-presence='buffering'],
  .presence[data-presence='autoplay-blocked'] {
    color: #ffd08a;
  }

  .presence[data-presence='disconnected'] {
    color: var(--wa-danger);
  }

  .suffix {
    color: var(--wa-text-dim);
    font-variant-numeric: tabular-nums;
    font-size: 0.85rem;
  }

  .badge {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    border: 1px solid var(--wa-border);
    border-radius: 0.25rem;
    padding: 0 0.3rem;
    margin-left: 0.3rem;
    color: var(--wa-text-dim);
    white-space: nowrap;
  }

  .badge.host {
    color: var(--wa-accent);
    border-color: var(--wa-accent);
  }

  button {
    flex-shrink: 0;
    padding: 0.3rem 0.5rem;
    font-size: 0.82rem;
  }

  .empty {
    margin: 0;
    color: var(--wa-text-dim);
  }
</style>
